import { useState, useRef, useEffect } from 'react';
import { User, Settings, LogOut, ChevronDown, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { Button } from './ui/Button';
import { supabase } from '../services/supabase';

export default function ProfileDropdown() {
  const { user, profile, signOut } = useAuthStore();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // @ts-ignore - TODO: fix this
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = async (action: string) => {
    setIsOpen(false);
    switch(action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'logout':
          try {
            await supabase.auth.signOut()
            signOut()
          } catch (error) {
            console.error('Error signing out:', error)
          }
          break;
      case 'admin':
        navigate('/admin');
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200"
      >
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
          <User size={18} className="text-white" />
        </div>
        <span className="text-gray-700 font-medium">{user?.user_metadata?.display_name ?? user?.email}</span>
        <ChevronDown
          size={16}
          className={`text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Profile Option */}
          <button
            onClick={() => handleMenuClick('profile')}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          >
            <User size={18} className="text-gray-500" />
            <span>{t('navigation.profile')}</span>
          </button>

          {/* Settings Option */}
          <button
            onClick={() => handleMenuClick('settings')}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          >
            <Settings size={18} className="text-gray-500" />
            <span>{t('navigation.settings')}</span>
          </button>

          {/* Admin Option */}
          {profile?.is_admin && (
            <button
              onClick={() => handleMenuClick('admin')}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-150"
            >
              <Shield size={18} className="text-gray-500" />
              <span>Admin Dashboard</span>
            </button>
          )}

           {/* Divider */}
           <div className="border-t border-gray-100 my-1"></div>


          {/* Logout Option */}
          <button
            onClick={() => handleMenuClick('logout')}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors duration-150"
          >
            <LogOut size={18} className="text-red-500" />
            <span>{t('auth.signOut')}</span>
          </button>
        </div>
      )}
    </div>
  );
}